import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api/auth";
import { toast } from "@/lib/toast";
import { APIError } from "@/lib/api/client";
import { useI18n } from "@/context/I18nContext";
import {
  profileChangePasswordSchema,
  type ProfileChangePasswordValues,
} from "../schemas/authSchemas";

export function useProfileChangePasswordForm() {
  const { logout } = useAuth();
  const router = useRouter();
  const { t } = useI18n();

  const form = useForm<ProfileChangePasswordValues>({
    resolver: zodResolver(profileChangePasswordSchema),
    defaultValues: { current_password: "", new_password: "", confirm_new_password: "" },
    mode: "onBlur",
  });

  const mutation = useMutation({
    mutationFn: (values: ProfileChangePasswordValues) =>
      authApi.changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      }),
    onSuccess: async () => {
      toast.success({ message: t("profile.changePassword.successToast") });
      await logout();
      router.replace("/(auth)/login");
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync(values).catch(() => {});
  });

  const mapError = useCallback((err: unknown): string => {
    if (err instanceof APIError) {
      const code = err.errorCode?.toUpperCase();
      const msg = err.message?.toLowerCase() ?? "";
      switch (code) {
        case "SAME_PASSWORD":
        case "NEW_PASSWORD_MUST_BE_DIFFERENT":
        case "PASSWORD_MUST_BE_DIFFERENT":
          return "auth.changePassword.error.samePassword";
        case "CURRENT_PASSWORD_INCORRECT":
        case "INVALID_CURRENT_PASSWORD":
        case "INCORRECT_CURRENT_PASSWORD":
          return "auth.changePassword.error.currentPasswordIncorrect";
      }
      // Backend returns plain-text detail — match on message content
      if (msg.includes("different")) {
        return "auth.changePassword.error.samePassword";
      }
      if (err.status === 400 || err.status === 401) {
        return "auth.changePassword.error.currentPasswordIncorrect";
      }
    }
    return "common.error.generic";
  }, []);

  const rawError = mutation.error;
  const error = rawError ? mapError(rawError) : null;

  return {
    form,
    isLoading: mutation.isPending,
    error,
    handleSubmit,
    reset: form.reset,
  };
}
